import React, { useEffect, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import * as S from '../Advances/AdvancesStyles';
import { getPendingRegularizations, approveRejectRegularization } from '../../../../services/attendanceService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const fmtDate = d => {
  if (!d) return '—';
  const parts = String(d).split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const fmtTime = t => (t ? new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');

const REQUESTED_STATUS_LABEL = {
  PRESENT: 'Present',
  HALF_DAY: 'Half Day',
  MISSED_PUNCH: 'Missed Punch',
  ON_DUTY: 'On Duty',
  TRAVEL: 'Travel',
  WEEKLY_OFF: 'Weekly Off',
};

/**
 * Regularization: "I missed punching in on a past date, let me mark it now and have my
 * admin approve it." Real backend as of regularization_controller.js on staging —
 * approving here auto-writes the actual Attendance record for that date, so this queue
 * is doing real work, not just cosmetic sign-off. The mobile submission flow (an
 * employee actually filing one of these) isn't built yet, so this queue will stay empty
 * until that lands — that's expected, not a bug in this tab.
 */
const RegularizationTab = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    setLoading(true);
    setRequests(await getPendingRegularizations());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = (req) => {
    Swal.fire({
      title: 'Approve this regularization?',
      text: `${req.user_id?.name || 'Staff'} · ${fmtDate(req.date)}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      confirmButtonColor: '#16a34a',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await approveRejectRegularization(req._id, 'APPROVED', '');
        toast.success('Regularization approved.');
        load();
      } catch (err) {
        toast.error('Failed to approve regularization.');
      }
    });
  };

  const handleReject = (req) => {
    Swal.fire({
      title: 'Reason for rejection:',
      input: 'text',
      inputPlaceholder: 'Enter reason…',
      showCancelButton: true,
      confirmButtonText: 'Confirm Rejection',
      confirmButtonColor: '#D5372A',
    }).then(async (result) => {
      if (result.value === undefined || result.value === null) return;
      try {
        await approveRejectRegularization(req._id, 'REJECTED', result.value);
        toast.info('Regularization rejected.');
        load();
      } catch (err) {
        toast.error('Failed to reject regularization.');
      }
    });
  };

  const columns = [
    {
      dataField: 'applied_at',
      text: 'Applied',
      sort: true,
      formatter: (cell) => (cell ? new Date(cell).toLocaleDateString('en-IN') : '—'),
    },
    {
      dataField: 'user_id',
      text: 'Staff',
      formatter: (cell) => <span className="member-link">{cell?.name || 'Staff'}</span>,
    },
    {
      dataField: 'reporting_to',
      text: 'Reporting To',
      formatter: (cell) => (cell?.name ? <S.Chip>{cell.name}</S.Chip> : <span className="rowmuted">—</span>),
    },
    { dataField: 'date', text: 'Date Being Corrected', formatter: (cell) => fmtDate(cell) },
    {
      dataField: 'claimed_time',
      text: 'Claimed In → Out',
      isDummyField: true,
      formatter: (cell, req) => `${fmtTime(req.in_time)} → ${fmtTime(req.out_time)}`,
    },
    {
      dataField: 'requested_status',
      text: 'Requested Status',
      formatter: (cell) => <S.Chip>{REQUESTED_STATUS_LABEL[cell] || cell || 'Present'}</S.Chip>,
    },
    { dataField: 'reason', text: 'Reason', formatter: (cell) => <span className="rowmuted">{cell || '—'}</span> },
    {
      dataField: 'decision',
      text: 'Decision',
      isDummyField: true,
      formatter: (cell, req) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <S.ActionIconBtn variant="approve" title="Approve" onClick={() => handleApprove(req)}>✓</S.ActionIconBtn>
          <S.ActionIconBtn variant="reject" title="Reject" onClick={() => handleReject(req)}>✕</S.ActionIconBtn>
        </div>
      ),
    },
  ];

  return (
    <div>
      <S.CardBox style={{ marginBottom: 14 }}>
        <strong style={{ fontSize: 16 }}>{requests.length}</strong> regularization request(s) waiting on approval
      </S.CardBox>

      <S.TableWrap>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
        ) : (
          <BootstrapTable
            keyField="_id"
            data={requests}
            columns={columns}
            bordered={false}
            hover
            noDataIndication={() => (
              <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                Nothing pending — you're all caught up.
              </div>
            )}
            pagination={paginationFactory({ sizePerPage: 50, sizePerPageList: [50, 100, 200], showTotal: true })}
          />
        )}
      </S.TableWrap>
    </div>
  );
};

export default RegularizationTab;
